import { styled } from '@styledComponents';
import { Flex } from '@components/Flex';
import { Button } from '@components/Button';
import { JoykitIcon } from '@components/JoykitIcon';
import { themeColor, themeMediaQuery, themeSpace, themeVariant } from '@utils/styledTheme.util';
import { animationTransition } from '@utils/animationTransition';
import { afterModalContentOpen, afterModalOverlayOpen, beforeModalContentClose, beforeModalOverlayClose } from '@components/StyledModal';

export const StyledButton = styled(Flex)`
  align-items: center;
  gap: ${themeSpace(1)};
  height: 100%;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0);

  z-index: 101;
  overflow: hidden;

  ${afterModalOverlayOpen()} {
    background: rgba(0, 0, 0, 0.48);
  }
  ${beforeModalOverlayClose()} {
    background: rgba(0, 0, 0, 0);
    pointer-events: none;
  }

  transition: ${animationTransition('background', 'opacity')};
`;

export const ModalContent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  align-items: center;
  justify-content: center;

  ${themeMediaQuery('below_sm')} {
    align-items: flex-end;
  }
`;

export const ModalHeader = styled(Flex)`
  align-items: center;
  justify-content: center;

  ${themeVariant('sectionEyeBrow24')};

  padding: 0px 16px;

  ${themeMediaQuery('below_sm')} {
    justify-content: flex-start;
    ${themeVariant('sectionEyeBrow20')};
  }
`;

export const ModalBody = styled.div`
  display: grid;
  padding: 0px 24px;
  row-gap: 16px;
  column-gap: 16px;

  align-self: stretch;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  grid-template-columns: repeat(3, minmax(0, 1fr));

  ${themeMediaQuery('below_sm')} {
    grid-template-rows: unset;
    grid-template-columns: unset;

    padding: 0;
  }
`;

export const Card = styled(Flex)`
  background-color: white;

  width: 640px;
  border-radius: ${themeSpace(5)};

  position: relative;

  opacity: 0;

  transition: ${animationTransition('transform', 'opacity')};

  ${afterModalContentOpen(ModalContent)} {
    opacity: 1;
  }

  ${beforeModalContentClose(ModalContent)} {
    opacity: 0;
  }

  ${themeMediaQuery('below_sm')} {
    width: 100%;

    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    transform: translateY(100%);
    opacity: 1;

    ${afterModalContentOpen(ModalContent)} {
      transform: translateY(0);
      opacity: 1;
    }

    ${beforeModalContentClose(ModalContent)} {
      transform: translateY(100%);
      opacity: 1;
    }
  }
`;

export const CloseButton = styled(Button)`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: ${themeSpace(3)};
  background: none;
  border: none;
  width: 60px;
  height: 60px;
  background: unset;
  border-radius: ${themeSpace(4)};
  display: flex;
  align-items: center;
  justify-content: center;

  transition: unset;

  &:hover {
    background: unset;
  }
`;

export const CloseIcon = styled(JoykitIcon)`
  width: 15px;
  height: 15px;

  ${themeMediaQuery('below_sm')} {
    width: 12px;
    height: 12px;
  }
`;

export const CardInnerContainer = styled(Flex)`
  width: 100%;
  height: 100%;
  padding: 16px;
  flex-direction: column;
  gap: 24px;

  ${themeMediaQuery('below_sm')} {
    padding: 12px;
    gap: 16px;
  }
`;

export const LanguageLink = styled.a<{ isSelected: boolean }>`
  display: flex;
  height: 48px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  background-color: white;
  color: ${themeColor('mono1')};

  border-radius: 8px;
  border: 2px solid ${({ isSelected }) => (isSelected ? themeColor('mono12') : 'transparent')};

  box-sizing: border-box;
  padding: 0 32px;
  text-align: center;
  white-space: nowrap;

  ${themeVariant('featureCopy17')};
  letter-spacing: 0;

  transition: ${animationTransition('color', 'background-color', 'border-color')};

  ${themeMediaQuery('sm')} {
    height: 48px;
    line-height: 46px;
    border-width: 1px;
  }

  ${themeMediaQuery('below_sm')} {
    justify-content: flex-start;
    padding: 0 16px;
  }

  &:hover {
    background-color: #ebebeb;
  }

  &:active {
    background-color: #d9d9d9;
    border: 2px solid ${themeColor('mono1')};
  }
`;

// Hidden language links for SEO - visually hidden but accessible to screen readers and crawlers
export const HiddenLanguageLinksContainer = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const HiddenLanguageLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:focus {
    position: static;
    width: auto;
    height: auto;
    padding: 4px 8px;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
    background-color: ${themeColor('mono12')};
    color: white;
    border-radius: 4px;
    outline: 2px solid ${themeColor('mono1')};
  }
`;
